import { useCallback, useEffect, useLayoutEffect, useRef, useState, type ReactNode } from 'react'
import { ImagePlus, Loader2, Send, Star, User, X } from 'lucide-react'
import * as Y from 'yjs'
import type { ChatMessage } from '../types'
import { generateId } from '../lib/id'
import { resolveAgentIdentity } from '../lib/agentIdentity'

/** Altura máxima do campo de mensagem (~8 linhas); acima disso ele rola. */
const INPUT_MAX_HEIGHT = 180

export interface ChatAttachment {
  id: string
  url: string
  name: string
  uploading?: boolean
}

interface ChatPanelProps {
  chatMessages: Y.Array<ChatMessage>
  currentUser?: { id: string; name: string } | null
  onSent?: (content: string) => void
  draft?: string
  onDraftConsumed?: () => void
  asking?: boolean
  /** Quando definido, o painel não grava a mensagem: entrega o texto para quem chamou (Studio). */
  onSubmit?: (content: string) => void
  title?: string
  placeholder?: string
  emptyHint?: ReactNode
  /** Resposta(s) do agente chegando em streaming (ainda fora do Yjs); várias quando há agentes em paralelo. */
  streaming?: StreamingReply | StreamingReply[] | null
  /** Conteúdo extra abaixo de uma mensagem (ex.: cartão do turno com "Desfazer"). */
  renderMeta?: (msg: ChatMessage) => ReactNode
  /** Substitui o texto de uma mensagem por um componente (ex.: cartão do plano do time). */
  renderBody?: (msg: ChatMessage, messages: ChatMessage[]) => ReactNode | undefined
  /** Conteúdo acima do campo de mensagem (ex.: modo planejamento e decisões do projeto). */
  composerTop?: ReactNode
  /** Anexos de imagem do composer. */
  attachments?: ChatAttachment[]
  onAttachImages?: (files: File[]) => void
  onRemoveAttachment?: (id: string) => void
  disabled?: boolean
  disabledReason?: string
}

export interface StreamingReply {
  key?: string
  name: string
  color?: string
  text: string
  activity?: string
}

export default function ChatPanel({
  chatMessages,
  currentUser,
  onSent,
  draft,
  onDraftConsumed,
  asking,
  onSubmit,
  title = 'Chat',
  placeholder = 'Mensagem… @Archie, @Dochie, @Jobs ou @Van Gogh',
  emptyHint,
  streaming,
  renderMeta,
  renderBody,
  composerTop,
  attachments,
  onAttachImages,
  onRemoveAttachment,
  disabled,
  disabledReason,
}: ChatPanelProps) {
  const [input, setInput] = useState('')
  const [messages, setMessages] = useState<ChatMessage[]>([])
  const bottomRef = useRef<HTMLDivElement>(null)
  const textareaRef = useRef<HTMLTextAreaElement>(null)
  const fileRef = useRef<HTMLInputElement>(null)

  useEffect(() => {
    const handler = () => {
      setMessages(chatMessages.toArray())
    }
    chatMessages.observe(handler)
    handler()
    return () => chatMessages.unobserve(handler)
  }, [chatMessages])

  const bubbles = streaming ? (Array.isArray(streaming) ? streaming : [streaming]) : []
  const streamingKey = bubbles.map((b) => `${b.key}:${b.text.length}:${b.activity}`).join('|')

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' })
  }, [messages, asking, streamingKey])

  useEffect(() => {
    if (!draft) return
    setInput(draft)
    onDraftConsumed?.()
    textareaRef.current?.focus()
  }, [draft, onDraftConsumed])

  const handleSend = () => {
    if (disabled) return
    if (attachments?.some((a) => a.uploading)) return
    if (!input.trim()) return
    const content = input.trim()
    if (onSubmit) {
      onSubmit(content)
      setInput('')
      return
    }
    const msg: ChatMessage = {
      id: generateId(),
      content,
      sender_id: currentUser?.id || 'me',
      sender_name: currentUser?.name || 'Você',
      sender_type: 'user',
      created_at: new Date().toISOString(),
    }
    chatMessages.push([msg])
    onSent?.(content)
    setInput('')
  }

  const handleKeyDown = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault()
      handleSend()
    }
  }

  // Altura pelo conteúdo (ou pelo placeholder, quando vazio), até o máximo; depois rola.
  const resizeInput = useCallback(() => {
    const el = textareaRef.current
    if (!el) return
    el.style.height = 'auto'
    let height = el.scrollHeight
    if (!el.value && el.placeholder) {
      el.value = el.placeholder
      height = el.scrollHeight
      el.value = ''
    }
    el.style.height = `${Math.min(height + 2, INPUT_MAX_HEIGHT)}px`
    el.style.overflowY = height + 2 > INPUT_MAX_HEIGHT ? 'auto' : 'hidden'
  }, [])

  useLayoutEffect(() => {
    resizeInput()
  }, [input, placeholder, resizeInput])

  useEffect(() => {
    const el = textareaRef.current
    if (!el || typeof ResizeObserver === 'undefined') return
    // A largura muda quando o painel é redimensionado: as linhas mudam junto.
    let width = el.clientWidth
    const observer = new ResizeObserver(() => {
      if (el.clientWidth === width) return
      width = el.clientWidth
      resizeInput()
    })
    observer.observe(el)
    return () => observer.disconnect()
  }, [resizeInput])

  const formatTime = (iso: string) => {
    try {
      return new Date(iso).toLocaleTimeString('pt-BR', { hour: '2-digit', minute: '2-digit' })
    } catch {
      return ''
    }
  }

  return (
    <div className="flex flex-col h-full">
      <div className="px-4 py-3 border-b border-gray-200 dark:border-gray-800">
        <h2 className="text-sm font-semibold text-gray-900 dark:text-white">{title}</h2>
      </div>

      <div className="flex-1 overflow-y-auto custom-scrollbar p-4 space-y-3">
        {messages.length === 0 && !bubbles.length && (
          emptyHint ?? (
            <div className="text-center text-sm text-gray-400 dark:text-gray-600 py-8">
              Nenhuma mensagem ainda.
              <br />
              Inicie a conversa ou chame um agente.
            </div>
          )
        )}

        {messages.map((msg) => {
          const isMe = msg.sender_id === (currentUser?.id || 'me')
          const isAgent = msg.sender_type === 'agent'
          const isSystem = msg.sender_type === 'system'

          if (isSystem) {
            return (
              <div key={msg.id} className="flex justify-center">
                <div className="text-[10px] text-gray-400 dark:text-gray-600 bg-gray-100 dark:bg-gray-800 px-2 py-1 rounded-full">
                  {msg.content}
                </div>
              </div>
            )
          }

          // Identidade visual do agente (cor da persona), como na lista de participantes
          const agentIdentity = isAgent ? resolveAgentIdentity(msg.sender_id, msg.sender_name) : null
          const agentColor = agentIdentity?.color
          const body = renderBody?.(msg, messages)

          return (
            <div key={msg.id} className={`flex gap-2 ${isMe ? 'flex-row-reverse' : ''}`}>
              <div
                className={`w-7 h-7 rounded-full flex items-center justify-center shrink-0 ${
                  isAgent ? '' : isMe ? 'bg-blue-500' : 'bg-gray-300 dark:bg-gray-700'
                }`}
                style={isAgent && agentColor ? { backgroundColor: agentColor } : undefined}
              >
                {isAgent ? (
                  <Star size={14} className="text-white" fill="currentColor" />
                ) : (
                  <User
                    size={14}
                    className={isMe ? 'text-white' : 'text-gray-600 dark:text-gray-300'}
                  />
                )}
              </div>
              <div
                className={`${body ? 'flex-1 min-w-0 max-w-[94%]' : 'max-w-[80%]'} px-3 py-2 rounded-lg text-sm ${
                  isAgent
                    ? 'border text-gray-800 dark:text-gray-100'
                    : isMe
                      ? 'bg-agent text-white'
                      : 'bg-gray-100 dark:bg-gray-800 text-gray-800 dark:text-gray-200'
                }`}
                style={
                  isAgent && agentColor
                    ? { backgroundColor: `${agentColor}14`, borderColor: `${agentColor}40` }
                    : undefined
                }
              >
                {!isMe && (
                  <div
                    className="text-xs font-semibold mb-0.5 flex items-center gap-1"
                    style={isAgent && agentColor ? { color: agentColor } : undefined}
                  >
                    {msg.sender_name}
                    {isAgent && <Star size={10} fill="currentColor" />}
                  </div>
                )}
                {body ?? (msg.content && <div className="whitespace-pre-wrap">{msg.content}</div>)}
                {renderMeta?.(msg)}
                <div className={`text-[10px] mt-1 opacity-60 text-right ${isMe ? 'text-white/70' : ''}`}>
                  {formatTime(msg.created_at)}
                </div>
              </div>
            </div>
          )
        })}
        {asking && (
          <div className="text-xs text-muted">Agente escrevendo...</div>
        )}
        {bubbles.map((bubble, i) => (
          <div key={bubble.key || i} className="flex gap-2">
            <div
              className="w-7 h-7 rounded-full flex items-center justify-center shrink-0"
              style={{ backgroundColor: bubble.color || '#6d3ae0' }}
            >
              <Star size={14} className="text-white" fill="currentColor" />
            </div>
            <div
              className="max-w-[80%] px-3 py-2 rounded-lg text-sm border text-gray-800 dark:text-gray-100"
              style={{ backgroundColor: `${bubble.color || '#6d3ae0'}14`, borderColor: `${bubble.color || '#6d3ae0'}40` }}
              aria-live="polite"
            >
              <div className="text-xs font-semibold mb-0.5" style={{ color: bubble.color }}>{bubble.name}</div>
              {bubble.text && <div className="whitespace-pre-wrap">{bubble.text}</div>}
              {bubble.activity && (
                <div className="flex items-center gap-1.5 text-xs text-muted mt-1">
                  <Loader2 size={12} className="animate-spin" />
                  {bubble.activity}…
                </div>
              )}
            </div>
          </div>
        ))}
        <div ref={bottomRef} />
      </div>

      <div
        className="p-3 border-t border-gray-200 dark:border-gray-800"
        onDragOver={(e) => {
          if (onAttachImages && e.dataTransfer.types.includes('Files')) e.preventDefault()
        }}
        onDrop={(e) => {
          if (!onAttachImages) return
          const files = Array.from(e.dataTransfer.files || []).filter((f) => f.type.startsWith('image/'))
          if (!files.length) return
          e.preventDefault()
          onAttachImages(files)
        }}
      >
        {composerTop}
        {disabled && disabledReason && (
          <div className="mb-2 text-xs text-muted flex items-center gap-1.5">
            <Loader2 size={12} className="animate-spin" />
            {disabledReason}
          </div>
        )}
        {attachments && attachments.length > 0 && (
          <div className="flex flex-wrap gap-2 mb-2">
            {attachments.map((a) => (
              <div key={a.id} className="relative w-14 h-14 rounded-md overflow-hidden border border-gray-200 dark:border-gray-700 bg-gray-100 dark:bg-gray-800">
                <img src={a.url} alt={a.name} className="w-full h-full object-cover" />
                {a.uploading && (
                  <div className="absolute inset-0 bg-black/40 flex items-center justify-center">
                    <Loader2 size={14} className="animate-spin text-white" />
                  </div>
                )}
                {onRemoveAttachment && (
                  <button
                    type="button"
                    onClick={() => onRemoveAttachment(a.id)}
                    aria-label={`Remover ${a.name}`}
                    className="absolute top-0.5 right-0.5 w-4 h-4 rounded-full bg-black/60 text-white flex items-center justify-center cursor-pointer"
                  >
                    <X size={10} />
                  </button>
                )}
              </div>
            ))}
          </div>
        )}
        <div className="flex gap-2 items-end">
          {onAttachImages && (
            <>
              <button
                type="button"
                onClick={() => fileRef.current?.click()}
                aria-label="Anexar imagem"
                title="Anexar imagem (ou cole/arraste aqui)"
                className="p-2.5 rounded-lg text-gray-500 hover:bg-gray-100 dark:hover:bg-gray-800 transition-colors shrink-0 cursor-pointer"
              >
                <ImagePlus size={16} aria-hidden="true" />
              </button>
              <input
                ref={fileRef}
                type="file"
                accept="image/png,image/jpeg,image/webp,image/gif"
                multiple
                className="hidden"
                onChange={(e) => {
                  const files = Array.from(e.target.files || [])
                  e.target.value = ''
                  if (files.length) onAttachImages(files)
                }}
              />
            </>
          )}
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={handleKeyDown}
            onPaste={(e) => {
              if (!onAttachImages) return
              const files = Array.from(e.clipboardData?.files || []).filter((f) => f.type.startsWith('image/'))
              if (!files.length) return
              e.preventDefault()
              onAttachImages(files)
            }}
            rows={1}
            placeholder={placeholder}
            aria-label="Mensagem do chat"
            className="flex-1 px-3 py-2 text-sm rounded-lg bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-500 outline-none focus:ring-2 focus:ring-agent resize-none min-h-[40px] leading-5"
          />
          <button
            onClick={handleSend}
            disabled={disabled}
            aria-label="Enviar mensagem"
            title="Enviar mensagem (Enter)"
            className="p-2.5 rounded-lg bg-agent text-white hover:bg-agent/90 transition-colors shrink-0 disabled:opacity-40"
          >
            <Send size={16} aria-hidden="true" />
          </button>
        </div>
      </div>
    </div>
  )
}
